import React, { FC, ReactElement, useMemo } from 'react'
import TextField from '@material-ui/core/TextField/TextField'
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress'
import SvgIcon from '@material-ui/core/SvgIcon'
import { ReactComponent as CircleArrowIcon } from 'assets/svg/CircleArrowBottomIcon.svg'
import Autocomplete, { AutocompleteChangeDetails, AutocompleteChangeReason } from '@material-ui/lab/Autocomplete/Autocomplete'
import { Company, CompanyLight, CompanyType, ICompaniesParamsLight } from 'typescript/interfaces/companies'
import { useQuery } from 'react-query'
import { getCompaniesLight } from 'services/companies'
import { Value } from '@material-ui/lab'
import { Chip } from '@material-ui/core'
import { useMultipleAutocompleteStyles } from 'components/RolesAutoComplete'
import { useSelectComponentStyle } from 'UI/Select'
import cn from 'classnames'

interface IAutoCompleteProps {
  companyId: any | any[]
  companyEntity?: Company
  onChange?: (
    event: React.ChangeEvent<{}>,
    value: Value<any, any, any, any>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any>,
  ) => void
  error?: boolean
  helperText?: string | false
  label?: string | ReactElement
  companyType?: CompanyType
  multiple?: boolean
  extraOptions?: boolean
  disabled?: boolean
  paramsRequest?: Partial<ICompaniesParamsLight>
  allTypes?: boolean
  className?: string
  smoothLeft?: boolean
  smoothRight?: boolean
}

const ALL_OPTION = { id: '__ALL__', name: 'All' }
const ADD_ALL_OPTION = { id: '__ALL__', name: 'Add All' }
const REMOVE_ALL_OPTION = { id: '__REMOVE_ALL__', name: 'Remove All' }

const CompanyAutoComplete: FC<IAutoCompleteProps> = ({
  companyId,
  onChange,
  helperText,
  error,
  label = 'Company',
  companyType = CompanyType.InHouse,
  multiple,
  extraOptions,
  disabled,
  paramsRequest = {},
  companyEntity,
  allTypes,
  className,
  smoothLeft,
  smoothRight,
}) => {

  const classes = useSelectComponentStyle()
  const mClasses = useMultipleAutocompleteStyles()

  const { data, isLoading } = useQuery(['companies'], () => {
    return getCompaniesLight({
      PageSize: 4000,
      ...(allTypes ? {} : { CompanyType: companyType }),
      ...paramsRequest,
    })
  })

  const extraOptionsList = useMemo(() => {
    if (!multiple || !extraOptions) return []
    if (Array.isArray(companyId)) return [ADD_ALL_OPTION, REMOVE_ALL_OPTION]
    return [ADD_ALL_OPTION]
  }, [multiple, extraOptions, companyId])

  const arrayCompanies = useMemo(() => {
    const dataCompanies: any[] = data ? data.data : []
    const baseList = [...extraOptionsList, ...dataCompanies]
    return companyEntity ? baseList.concat({ id: companyEntity.id, name: companyEntity.name }) : baseList
  }, [data, companyEntity, extraOptionsList])

  const selectedOptionCompany = useMemo(() => {
    return data ? arrayCompanies.find((c) => c.id === companyId) || null : null
  }, [arrayCompanies, companyId])

  const arraySelectedOptions = useMemo(() => {
    if (!Array.isArray(companyId) || !data) return []
    if (extraOptions && companyId.length === 0) return [{ ...ALL_OPTION }]
    return arrayCompanies.filter((c) => companyId.includes(c.id))
  }, [arrayCompanies, companyId, data])

  const handleChange = (
    event: React.ChangeEvent<{}>,
    value: Value<any, any, any, any>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any>,
  ) => {
    if (!multiple || !extraOptions || !Array.isArray(value)) {
      onChange?.(event, value, reason, details)
      return
    }

    const ids = value.map((v) => v.id)
    if (ids.includes(REMOVE_ALL_OPTION.id)) onChange?.(event, undefined, reason, details)
    else if (ids.includes(ADD_ALL_OPTION.id)) onChange?.(event, [], reason, details)
    else if (value.length === 0) onChange?.(event, undefined, reason, details)
    else onChange?.(event, value, reason, details)
  }

  return (
    <Autocomplete
      disabled={disabled}
      multiple={Boolean(multiple)}
      value={multiple ? arraySelectedOptions : selectedOptionCompany}
      loading={isLoading}
      options={arrayCompanies}
      classes={{ inputRoot: cn(mClasses.inputRoot, multiple), root: className }}
      renderTags={(tagValue, getTagProps) => {
        return tagValue.map((option: CompanyLight, index) => <Chip label={option.name} {...getTagProps({ index })} />)
      }}
      getOptionLabel={(option: CompanyLight) => {
        return option.name
      }}
      getOptionSelected={(option: CompanyLight, value) => {
        if (value && value.id) return option.id === value.id
        return false
      }}
      onChange={handleChange}
      renderInput={(params) => (
        <TextField
          {...params}
          autoComplete="off"
          helperText={helperText}
          error={error}
          label={label}
          className={cn({ [classes.disabledFormControlLeftBorder]: smoothLeft }, { [classes.disabledFormControlRightBorder]: smoothRight })}
          InputProps={{
            ...params.InputProps,
            ref: params.InputProps.ref,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                <SvgIcon fontSize="inherit" viewBox="0 0 20 20" component={CircleArrowIcon} />
              </>
            ),
          }}
          variant="outlined"
        />
      )}
    />
  )
}

export default CompanyAutoComplete
